import {useLocation} from '@tanstack/react-router';
import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useQualityAgentAccessQuery} from '~api/auth.queries';
import {useFeatureFlags} from '~utils/featureFlags';
import {selectUserEmail} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {AskAiAgent, getAgentForPathname} from './askAiAgents';
import {ASK_AI_OPEN_EVENT, AskAiOpenReason} from './askAiEvents';
import {readStoredPanel, storePanel} from './askAiPanelStorage';
import {disposeAskAiWidget, getAskAiWidgetBaseUrl, loadAskAiWidget} from './askAiWidget';

export type AskAiLayout = 'floating' | 'sidebar' | 'fullscreen';

const ASK_AI_LAYOUT_STORAGE_KEY = 'askAiLayout';

function readStoredLayout(): AskAiLayout {
  try {
    const stored = localStorage.getItem(ASK_AI_LAYOUT_STORAGE_KEY);
    if (stored === 'floating' || stored === 'sidebar' || stored === 'fullscreen') {
      return stored;
    }
  } catch {
    return 'floating';
  }
  return 'floating';
}

interface AskAiContextValue {
  isEnabled: boolean;
  isQualityEnabled: boolean;
  isOpen: boolean;
  agent: AskAiAgent;
  layout: AskAiLayout;
  openPanel: (agent?: AskAiAgent) => void;
  closePanel: () => void;
  togglePanel: (agent?: AskAiAgent) => void;
  setLayout: (layout: AskAiLayout) => void;
}

const AskAiContext = createContext<AskAiContextValue>({
  isEnabled: false,
  isQualityEnabled: false,
  isOpen: false,
  agent: 'vision',
  layout: 'floating',
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
  setLayout: () => undefined,
});

export function AskAiProvider({children}: {children: ReactNode}) {
  const {toggleEnableAskAiWidget} = useFeatureFlags();
  const userEmail = useAppSelector(selectUserEmail);
  const isEnabled = Boolean(getAskAiWidgetBaseUrl()) && Boolean(toggleEnableAskAiWidget);
  const isQualityDeployed = Boolean(import.meta.env.VITE_QUALITY_AGENT_URL);
  const qualityAccess = useQualityAgentAccessQuery(isQualityDeployed && Boolean(userEmail));
  const isQualityEnabled = isQualityDeployed && qualityAccess.isSuccess;

  const pathname = useLocation({select: (location) => location.pathname});
  const restoredPanel = useRef(readStoredPanel()).current;
  const [isOpen, setIsOpen] = useState(restoredPanel.open && restoredPanel.agent === getAgentForPathname(pathname));
  const [agent, setAgent] = useState<AskAiAgent>(restoredPanel.agent);
  const [layout, setLayoutState] = useState<AskAiLayout>(readStoredLayout);
  const previousPathnameRef = useRef(pathname);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    void loadAskAiWidget();
    return () => disposeAskAiWidget();
  }, [isEnabled]);

  const openPanel = useCallback((nextAgent: AskAiAgent = 'vision') => {
    setAgent(nextAgent);
    setIsOpen(true);
  }, []);
  const closePanel = useCallback(() => setIsOpen(false), []);
  const togglePanel = useCallback(
    (nextAgent: AskAiAgent = 'vision') => {
      if (isOpen && agent === nextAgent) {
        setIsOpen(false);
        return;
      }
      openPanel(nextAgent);
    },
    [isOpen, agent, openPanel],
  );

  const setLayout = useCallback((nextLayout: AskAiLayout) => {
    setLayoutState(nextLayout);
    try {
      localStorage.setItem(ASK_AI_LAYOUT_STORAGE_KEY, nextLayout);
    } catch {
      return;
    }
  }, []);

  useEffect(() => {
    if (userEmail && restoredPanel.user && restoredPanel.user !== userEmail) {
      setIsOpen(false);
    }
  }, [userEmail, restoredPanel.user]);

  useEffect(() => {
    storePanel({open: isOpen, agent, user: userEmail ?? ''});
  }, [isOpen, agent, userEmail]);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }
    previousPathnameRef.current = pathname;
    if (!isOpen) {
      return;
    }
    if (layout === 'fullscreen') {
      setLayout('floating');
    }
    if (getAgentForPathname(pathname) !== agent) {
      setIsOpen(false);
    }
  }, [pathname, isOpen, agent, layout, setLayout]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const handleOpenEvent = (event: Event) => {
      const reason = (event as CustomEvent<{reason?: AskAiOpenReason}>).detail?.reason;
      if (reason !== 'chat_link') {
        return;
      }
      if (layoutRef.current === 'fullscreen') {
        setLayout('floating');
      }
      setAgent('vision');
      setIsOpen(true);
    };
    window.addEventListener(ASK_AI_OPEN_EVENT, handleOpenEvent);
    return () => window.removeEventListener(ASK_AI_OPEN_EVENT, handleOpenEvent);
  }, [isEnabled, pathname, setLayout]);

  const value = useMemo(
    () => ({isEnabled, isQualityEnabled, isOpen, agent, layout, openPanel, closePanel, togglePanel, setLayout}),
    [isEnabled, isQualityEnabled, isOpen, agent, layout, openPanel, closePanel, togglePanel, setLayout],
  );

  return <AskAiContext.Provider value={value}>{children}</AskAiContext.Provider>;
}

export function useAskAi() {
  return useContext(AskAiContext);
}
